import React, { useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { useGetPageContentQuery } from "../api/businessApi";
import {
  HiOutlineLightBulb,
  HiOutlineScale,
  HiOutlineCurrencyDollar,
  HiOutlineSpeakerphone,
  HiOutlineCog,
  HiOutlineHeart,
  HiOutlineUserGroup,
  HiOutlineGlobe,
  HiOutlineCalendar,
  HiOutlineBookOpen,
  HiOutlineExternalLink
} from "react-icons/hi";

const Advisory = () => {
  const { data: pageData } = useGetPageContentQuery("advisory");

  const getSection = useCallback((key, fallback) => {
    const section = pageData?.sections?.find((s) => s.section_key === key);
    return section?.content || fallback;
  }, [pageData]);

  // Hero content
  const heroContent = useMemo(() => getSection("hero", {
    badge_text: "Advisory Committee",
    heading: "Advisory Committee \u2014",
    heading_accent: "Tijarat Raabehah Chicago",
    subtitle: "Empowering Dawoodi Bohra Entrepreneurs in Chicagoland and beyond",
    body: "The Advisory Committee is your local gateway within Tijarat Raabehah Chicago, offering holistic support\u2014from ideation and setup to legal, financial, marketing, and operational growth\u2014with values rooted in community, ethics, and sustainable success.",
  }), [getSection]);

  // Mission content
  const missionContent = useMemo(() => getSection("mission", {
    heading: "Our Mission",
    body: "Under the guidance of Syedna Mufaddal Saifuddin TUS, we empower the Dawoodi Bohra community to build profitable and sustainable businesses in the Chicago area\u2014grounded in moral integrity, modern practices, and collaborative strength.",
  }), [getSection]);

  // CTA content
  const ctaContent = useMemo(() => getSection("cta", {
    heading: "Ready to Transform Your Business Idea?",
    body: "Join our community of successful entrepreneurs and let our advisory committee guide you towards sustainable business success.",
    button_text: "Schedule Your Consultation",
    button_link: "https://calendar.app.google/aA6HSupCUZrrszhd7",
  }), [getSection]);

  const servicesIcons = [
    <HiOutlineLightBulb className="w-8 h-8" />,
    <HiOutlineScale className="w-8 h-8" />,
    <HiOutlineCurrencyDollar className="w-8 h-8" />,
    <HiOutlineSpeakerphone className="w-8 h-8" />,
    <HiOutlineCog className="w-8 h-8" />,
  ];

  const servicesContent = useMemo(() => getSection("services", {
    heading: "Our Services",
    subtitle: "Comprehensive business support services designed to help you succeed at every stage of your entrepreneurial journey.",
    items: [
      { title: "Business Idea Development & Strategic Planning", description: "Transform your vision into a concrete business strategy with comprehensive planning and market analysis." },
      { title: "Legal Setup, Compliance & Entity Structuring", description: "Navigate legal requirements, business registration, and compliance with expert guidance." },
      { title: "Financial Strategies & Planning", description: "Budgeting, financing options, and household financial planning to ensure sustainable growth." },
      { title: "Marketing & Sales Support", description: "Digital marketing, social media strategies, and branding to reach your target audience effectively." },
      { title: "Digital Transformation & Operations", description: "Streamline operations and embrace digital tools for improved efficiency and growth." },
    ],
  }), [getSection]);

  const services = useMemo(() =>
    (servicesContent.items || []).map((item, index) => ({
      ...item,
      icon: servicesIcons[index] || <HiOutlineLightBulb className="w-8 h-8" />,
    })),
    [servicesContent]
  );

  const whyIcons = [
    <HiOutlineHeart className="w-6 h-6" />,
    <HiOutlineUserGroup className="w-6 h-6" />,
    <HiOutlineGlobe className="w-6 h-6" />,
  ];

  const whyContent = useMemo(() => getSection("why_work_with_us", {
    heading: "Why Work with Us?",
    items: [
      { title: "Ethical Integrity", description: "Rooted in Shariyat Mohammediyah values and moral business practices" },
      { title: "Inclusive Support", description: "Supporting startups, established businesses, women entrepreneurs, and home-based enterprises" },
      { title: "Network Access", description: "Connect to local and international networks, trade opportunities, and community insights" },
    ],
  }), [getSection]);

  const whyWorkWithUs = useMemo(() =>
    (whyContent.items || []).map((item, index) => ({
      ...item,
      icon: whyIcons[index] || <HiOutlineHeart className="w-6 h-6" />,
    })),
    [whyContent]
  );

  const stepsIcons = [
    <HiOutlineBookOpen className="w-6 h-6" />,
    <HiOutlineCalendar className="w-6 h-6" />,
    <HiOutlineUserGroup className="w-6 h-6" />,
  ];

  const stepsContent = useMemo(() => getSection("get_started", {
    heading: "Get Started",
    subtitle: "Ready to begin your entrepreneurial journey? Here are three simple steps to get started with our advisory services.",
    items: [
      { title: "Check for Support in the Business Directory", description: "Look for individuals and businesses in your industry for support and collaboration", link: "/directory", linkText: "Browse Directory", external: false },
      { title: "Schedule a Consultation", description: "Book a meeting to brainstorm or get started with your business journey", link: "https://calendar.app.google/aA6HSupCUZrrszhd7", linkText: "Book Meeting", external: true },
      { title: "Join Workshops & Networking Events", description: "Learn, grow, and connect with fellow entrepreneurs in our community", link: "/events", linkText: "View Events", external: false },
    ],
  }), [getSection]);

  const getStartedSteps = useMemo(() =>
    (stepsContent.items || []).map((item, index) => ({
      ...item,
      icon: stepsIcons[index] || <HiOutlineBookOpen className="w-6 h-6" />,
    })),
    [stepsContent]
  );

  return (
    <main className="min-h-screen bg-white" role="main">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#135879] via-[#1a6b8f] to-[#135879] py-12 sm:py-16 md:py-20 px-4 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
          <div className="absolute -top-1/4 -right-1/4 w-[300px] h-[300px] sm:w-[400px] sm:h-[400px] md:w-[600px] md:h-[600px] bg-gradient-radial from-[#f8d56b]/20 via-transparent to-transparent rounded-full animate-pulse opacity-60"></div>
          <div className="absolute -bottom-1/4 -left-1/4 w-[200px] h-[200px] sm:w-[300px] sm:h-[300px] md:w-[400px] md:h-[400px] bg-gradient-radial from-[#f8d56b]/15 via-transparent to-transparent rounded-full animate-pulse opacity-40" style={{animationDelay: '1s'}}></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center px-3 py-2 sm:px-4 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-4 sm:mb-6">
            <span className="h-2 w-2 rounded-full bg-[#f8d56b] mr-2"></span>
            <span className="text-xs sm:text-sm font-medium text-white">{heroContent.badge_text}</span>
          </div>

          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 leading-tight px-4">
            {heroContent.heading}{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#f8d56b] to-[#f9b208] block sm:inline">
              {heroContent.heading_accent}
            </span>
          </h1>

          <p className="text-lg sm:text-xl md:text-2xl text-gray-200 mb-6 sm:mb-8 max-w-4xl mx-auto leading-relaxed px-4">
            {heroContent.subtitle}
          </p>

          <p className="text-base sm:text-lg text-gray-300 max-w-5xl mx-auto leading-relaxed px-4">
            {heroContent.body}
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#135879] mb-4 sm:mb-6">{missionContent.heading}</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-gradient-to-r from-[#d4af37] to-[#f9b208] mx-auto mb-6 sm:mb-8 rounded-full"></div>
          </div>

          <div className="bg-white p-6 sm:p-8 md:p-12 rounded-2xl sm:rounded-3xl shadow-xl border border-gray-100">
            <div className="text-center">
              <div className="text-[#135879] mb-4 sm:mb-6 flex justify-center">
                <HiOutlineHeart className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16" />
              </div>
              <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed max-w-4xl mx-auto">
                {missionContent.body}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 max-w-7xl mx-auto">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#135879] mb-4 sm:mb-6">{servicesContent.heading || "Our Services"}</h2>
          <div className="w-16 sm:w-20 md:w-24 h-1 bg-gradient-to-r from-[#d4af37] to-[#f9b208] mx-auto mb-6 sm:mb-8 rounded-full"></div>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto px-4">
            {servicesContent.subtitle || "Comprehensive business support services designed to help you succeed at every stage of your entrepreneurial journey."}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="group bg-white p-6 sm:p-8 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
            >
              <div className="text-[#135879] mb-4 sm:mb-6 flex justify-center group-hover:scale-110 transition-transform duration-300">
                <div className="w-6 h-6 sm:w-8 sm:h-8">{service.icon}</div>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-[#135879] mb-3 sm:mb-4 text-center">
                {service.title}
              </h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed text-center">
                {service.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Why Work With Us Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#135879] mb-4 sm:mb-6">{whyContent.heading || "Why Work with Us?"}</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-gradient-to-r from-[#d4af37] to-[#f9b208] mx-auto mb-6 sm:mb-8 rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {whyWorkWithUs.map((item, index) => (
              <div
                key={index}
                className="text-center p-6 sm:p-8 bg-white rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="text-[#135879] mb-3 sm:mb-4 flex justify-center">
                  <div className="w-5 h-5 sm:w-6 sm:h-6">{item.icon}</div>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-[#135879] mb-2 sm:mb-3">
                  {item.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Get Started Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 max-w-7xl mx-auto">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#135879] mb-4 sm:mb-6">{stepsContent.heading || "Get Started"}</h2>
          <div className="w-16 sm:w-20 md:w-24 h-1 bg-gradient-to-r from-[#d4af37] to-[#f9b208] mx-auto mb-6 sm:mb-8 rounded-full"></div>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto px-4">
            {stepsContent.subtitle || "Ready to begin your entrepreneurial journey? Here are three simple steps to get started with our advisory services."}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {getStartedSteps.map((step, index) => (
            <div
              key={index}
              className="group bg-white p-6 sm:p-8 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100"
            >
              <div className="text-[#135879] mb-4 sm:mb-6 flex justify-center">
                <div className="w-5 h-5 sm:w-6 sm:h-6">{step.icon}</div>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-[#135879] mb-3 sm:mb-4 text-center">
                {step.title}
              </h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed text-center mb-4 sm:mb-6">
                {step.description}
              </p>
              <div className="text-center">
                {step.external ? (
                  <a
                    href={step.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-[#135879] to-[#1a6b8f] hover:from-[#0e4562] hover:to-[#135879] text-white font-semibold py-2.5 px-4 sm:py-3 sm:px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl text-sm sm:text-base"
                  >
                    <span>{step.linkText}</span>
                    <HiOutlineExternalLink className="w-3 h-3 sm:w-4 sm:h-4" />
                  </a>
                ) : (
                  <Link
                    to={step.link}
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-[#135879] to-[#1a6b8f] hover:from-[#0e4562] hover:to-[#135879] text-white font-semibold py-2.5 px-4 sm:py-3 sm:px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl text-sm sm:text-base"
                  >
                    <span>{step.linkText}</span>
                    <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4">
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-[#135879] to-[#0e4562] rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 shadow-2xl">
          <div className="text-[#d4af37] mb-6 sm:mb-8 flex justify-center">
            <HiOutlineCalendar className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16" />
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
            {ctaContent.heading}
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-200 mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed px-4">
            {ctaContent.body}
          </p>
          <a
            href={ctaContent.button_link || "https://calendar.app.google/aA6HSupCUZrrszhd7"}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 sm:gap-3 bg-gradient-to-r from-[#f8d56b] to-[#f9b208] hover:from-[#f9b208] hover:to-[#d4af37] text-[#135879] font-bold py-3 px-6 sm:py-4 sm:px-8 rounded-lg sm:rounded-xl transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl group text-sm sm:text-base"
          >
            <span>{ctaContent.button_text || "Schedule Your Consultation"}</span>
            <HiOutlineExternalLink className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </section>
    </main>
  );
};

export default Advisory;
